import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { Role } from '../models/role';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class RolesService extends CrudService implements AdvancedService<Role> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<Role>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<Role>(Role, x);
                return res;
            })
        );
    }

    ListAttributable(): Observable<Results<Role>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', 0);
        params.set('size', 100);
        const httpOptions = this.getListHttpOptions(null, null, null, null, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/AttributableRoles`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<Role>(Role, x);
                return res;
            })
        );
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    update(roleId: number, role: Role): Observable<Role> {
        // Appel de la Web Api
        return this.http.put<Role>(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles/${roleId}`, role, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Role.from(x);
            })
        );
    }

    delete(roleId: number): Observable<boolean> {
        // Appel de la Web Api
        return this.http.delete<Role>(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles/${roleId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return true;
            })
        );
    }

    add(role: Role): Observable<Role> {
        // Mappage pour l'organisation
        role.organizationId = this.organizationId;

        // Appel de la Web Api
        return this.http.post<Role>(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles`, role, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Role.from(x);
            })
        );
    }

    get(roleId: number): Observable<Role> {
        // Appel de la Web Api
        return this.http.get<Role>(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles/${roleId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Role.from(x);
            })
        );
    }

    /**
     * Exporte les droits d'un rôle.
     * @param roleId Id du rôle.
     */
    exportRights(roleId: number): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Roles/${roleId}/rights/export`, { responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }
}
